import { useState } from 'react'
import { useHillEvents } from './mqtt.jsx'

/**
 * Tracks every rider who's appeared on the hill this event, built from the
 * raw `hill` event stream (online/started/split/finished/flagged). Ported
 * from NIGAN_MQTT/web/live.html's per-card DOM state machine, reworked as a
 * single React-owned Map instead of N ad-hoc setInterval-driven DOM nodes.
 *
 * Only NIGAN's hardware-timed venues ever emit `online`/`started`/`split` -
 * poll-based venues (the Python timing-bridge publisher) only ever emit
 * `finished`/`flagged`. That's not special-cased here: a card that never
 * saw `online`/`started` just starts life at `finished`/`flagged` directly,
 * which the UI renders as "Just finished"/"Flagged" rather than implying it
 * was ever shown live - correct either way with no venue-type branch.
 *
 * Returns an array in newest-arrival-first order (whoever most recently
 * started a run is first) - a card's position is stable and only changes
 * when a rider starts a genuinely new run (their own or someone else's),
 * never as a side effect of an already-shown run's own status changing.
 * Deliberately NOT sorted by status (e.g. "running before finished") -
 * that reshuffles two cards' positions the instant either one's status
 * changes, which reads as cards randomly swapping/jumping rather than a
 * rider predictably arriving at the top and sinking down.
 *
 * "A new run" - not "a new rider" - is what moves a card to the front. A
 * plain `Map.set()` on an existing key updates its value in place without
 * moving it in iteration order, which is exactly what's wanted *within* one
 * run (a running card's splits/finish updating shouldn't reorder anything),
 * but is wrong across runs: once every rider in the field had appeared
 * once, a repeat rider's second/third/fourth-session run would update their
 * card at its original (long since scrolled-off) position instead of
 * coming back to the front - the hero panel would silently freeze onto
 * whichever two riders happened to run last in the first lap through the
 * field, with everyone else's later runs invisible. Deleting the key before
 * re-setting it (only when the event actually starts a new run - see below)
 * moves it to the end of the Map's iteration order, i.e. the front once
 * reversed.
 *
 * A finished/flagged card is never auto-removed on a timer - it just
 * stays until a genuinely new run's arrival pushes it out of the top
 * couple of entries (all the caller ever renders anyway). A linger-then-
 * remove timer was tried and reverted twice: it meant the pool of "who's
 * currently shown" could shrink out from under an UNRELATED still-running
 * rider's card the moment the other one's timer fired, snapping it from
 * the bottom slot to the top slot with nothing that rider actually did
 * causing it - exactly the "jumping" this hook's ordering is otherwise
 * built to avoid. Leaving a stale result on screen indefinitely is a far
 * smaller cost than that.
 */
export function useHillActivity() {
  const [cards, setCards] = useState(new Map())

  useHillEvents((evt) => {
    setCards((prev) => {
      const next = new Map(prev)
      const existing = next.get(evt.num)

      switch (evt.event) {
        case 'online':
          // Always the start of a new run - re-front unconditionally,
          // first-ever appearance or not.
          next.delete(evt.num)
          next.set(evt.num, {
            num: evt.num,
            rider: evt.rider,
            machine: evt.machine,
            class: evt.class,
            status: 'online',
            startedAt: null,
            splits: [],
            finalTime: null,
            session: null,
          })
          break
        case 'started':
          // Spread the existing card first, then only overwrite what
          // `started` actually carries - `class`/`machine` (only ever sent
          // on `online`, per MQTT_SCHEMA.md) survive instead of being
          // clobbered with undefined. Doesn't move position - `online`
          // already did that for this run.
          next.set(evt.num, {
            ...(existing || { num: evt.num, rider: evt.rider, machine: evt.machine, class: evt.class }),
            status: 'running',
            startedAt: evt.timestamp,
            splits: [],
          })
          break
        case 'split':
          if (existing) next.set(evt.num, { ...existing, splits: [...existing.splits, evt] })
          // else: no card yet (poll-based venues never send splits at all;
          // or a `started`/`online` was missed) - nothing to attach it to.
          break
        case 'finished':
        case 'flagged': {
          // On a NIGAN venue this run's `online` already re-fronted the
          // card, so an in-place update is correct here (moving it again
          // would reorder two already-shown cards mid-run, the exact
          // jumping this hook avoids elsewhere). On a poll-based venue
          // there's no `online` - every `finished`/`flagged` IS the whole
          // run, so once the existing card is itself already a finished
          // result, this new one is a genuinely new run and needs to
          // re-front the same way `online` does.
          const isNewRun = !existing || existing.status === 'finished' || existing.status === 'flagged'
          if (isNewRun) next.delete(evt.num)
          next.set(evt.num, {
            ...(existing || { num: evt.num, rider: evt.rider, machine: evt.machine, class: evt.class, splits: [] }),
            status: evt.event,
            finalTime: evt.time ?? null,
            session: evt.session ?? null,
          })
          break
        }
        default:
          break
      }
      return next
    })
  })

  // Map iteration order is insertion order; re-`.set()`ing an existing key
  // (every status update within one run) doesn't move it, but each case
  // above deletes-then-reinserts whenever the event actually starts a new
  // run - so this is genuinely "most-recent-run-first", reversed to put
  // the newest arrival first.
  return [...cards.values()].reverse()
}
